import { useReleaseNotes } from "@Context/common/release-notes/release-notes-context";
import { ReleaseNoteRecord } from "@Shared/schemas/release-notes/releaseNoteSchemas";
import useI18n from "@i18n";
import { Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import BybDialog from "../common/dialog/byb-vr-dialog";
import { StyledMarkdownContent } from "./vr-release-note-dialog.styled";

interface IVRReleaseNoteDialog {
  open: boolean;
  onClose: () => void;
  // Either provide a complete releaseNote object
  releaseNote?: ReleaseNoteRecord;
  // Or provide individual properties
  id?: number;
  version?: string;
  description?: string;
  createdAt?: string;
}

const VRReleaseNoteDialog = ({
  open,
  onClose,
  releaseNote,
  id: propId,
  version: propVersion,
  description: propDescription,
  createdAt: propCreatedAt,
}: IVRReleaseNoteDialog) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { i18n } = useI18n();
  const { markAsSeen, processReleaseNoteImages } = useReleaseNotes();

  // Use either the releaseNote object or individual props
  const id = releaseNote?.id || propId;
  const version = releaseNote?.version || propVersion;
  const description = releaseNote?.description || propDescription;
  const createdAt = releaseNote?.created_at || propCreatedAt;

  // Handle dialog close
  const handleClose = () => {
    // Mark the release note as seen when dialog is closed
    if (id) {
      markAsSeen(id, version);
    }
    onClose();
  };

  useEffect(() => {
    if (contentRef.current && description) {
      processReleaseNoteImages(contentRef.current);
    }
  }, [description, open, processReleaseNoteImages]);

  if (!id || !version || !description || !createdAt) {
    return null;
  }

  const formattedDate = new Date(createdAt).toLocaleDateString();
  const titleText = i18n(`release-notes.dialog.version`, { version });

  return (
    <BybDialog name="release-note" open={open} onClose={handleClose} width="80vw" title={titleText} showCloseButton>
      <Typography variant="textBase400" color="dsNeutral.v300">
        {formattedDate}
      </Typography>
      <StyledMarkdownContent>
        <div ref={contentRef} dangerouslySetInnerHTML={{ __html: description }} />
      </StyledMarkdownContent>
    </BybDialog>
  );
};

export default VRReleaseNoteDialog;
